import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiX, HiDownload, HiOutlineDocument, HiOutlineInformationCircle } from "react-icons/hi";
import FileIcon from "./FileIcon";

const DownloadConfirmModal = ({ isOpen, onClose, file, onConfirm }) => {
  if (!file) return null;

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl overflow-hidden"
          >
            <div className="p-8">
              {/* Header Icon */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="w-24 h-24 bg-indigo-50 rounded-[2rem] flex items-center justify-center text-indigo-600 shadow-inner">
                    <FileIcon extension={file.extension} className="w-10 h-10" />
                  </div>
                  <div className="absolute -bottom-2 -right-2 bg-white p-2 rounded-xl shadow-lg border border-slate-100">
                    <HiDownload className="text-indigo-600" size={20} />
                  </div>
                </div>
              </div>

              {/* Text Content */}
              <div className="text-center mb-8">
                <h3 className="text-xl font-black text-slate-800 mb-2 px-4 leading-tight">
                  Download File?
                </h3>
                <p className="text-sm text-slate-500 font-medium px-6">
                  Are you sure you want to download <span className="text-indigo-600 font-bold">"{file.name}"</span> to your device?
                </p>
              </div>

              {/* File Info Card */}
              <div className="bg-slate-50 rounded-2xl p-4 mb-8 border border-slate-100 flex items-center gap-4">
                <div className="p-3 bg-white rounded-xl shadow-sm border border-slate-200">
                  <HiOutlineInformationCircle size={20} className="text-slate-400" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">File Size</span>
                  <span className="text-sm font-black text-slate-700">{formatFileSize(file.size)}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-6 py-4 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-2xl font-bold transition-all active:scale-95 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onConfirm();
                    onClose();
                  }}
                  className="flex-2 px-10 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-bold shadow-lg shadow-indigo-100 transition-all active:scale-95 flex items-center justify-center gap-2 text-sm"
                >
                  <HiDownload size={18} />
                  Download Now
                </button>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all"
            >
              <HiX size={20} />
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default DownloadConfirmModal;
